import { Component, Input, OnInit } from "@angular/core";
import { GraphComponentComponent } from "../../canvas/graph-component/graph-component.component";
import { Tutorial, TutorialStep } from "../../common/help-model";
import { CURRENT_STATE_PROJECT_KEY, Linqset, NODE_SUPERTYPE, TUTORIAL_MODEL_KEY } from "../../common/linqset-model";
import { processLinqset } from "../../common/model-processing";
import { ONBOARDING_STEPS } from "../../common/user";
import { RepositoryService } from "../../linq-utils/repository.service";
import { MaterialModule } from "../../material/material.module";
import { VideoComponent } from "../../ui-components/video/video.component";
import { UserService } from "../../user/user.service";
import { Organisation } from "../../common/organisation";

const COMPONENT = "TutorialComponent"

// where each tutorial was left, in this browser, so a tutorial can be continued and finished ones are marked
const PROGRESS_KEY = 'linq.tutorials.progress'
type Progress = Record<string, {step:number, done?:boolean}>

const readProgress = ():Progress => {
    try { return JSON.parse(localStorage.getItem(PROGRESS_KEY) || '{}') || {} } catch { return {} }
}

const writeProgress = (progress:Progress) => {
    try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress)) } catch { /* it's only a convenience */ }
}

// each step's html has its title and summary first, then what to do inside <hidden-text>
const splitStepHtml = (html:string) => {
    const match = /<hidden-text>([\s\S]*?)<\/hidden-text>/.exec(html || '')
    const visible = match ? html.slice(0, match.index) + html.slice(match.index + match[0].length) : (html || '')
    // the title is shown on its own, so it's taken out of the summary
    return {summary: visible.replace(/<h2>[\s\S]*?<\/h2>/, '').trim(), script: match ? match[1].trim() : ''}
}

// the panel counts the steps itself, so a step's own number ("Step 1: ...") is left out of its title
// The name is read from the step's heading, here too in case the server didn't
const stepTitle = (step:TutorialStep|undefined) => {
    const name = step?.name || /<h2>([\s\S]*?)<\/h2>/.exec(step?.html || '')?.[1] || ''
    return name.replace(/^\s*Step\s*\d+\s*:\s*/i, '').trim()
}

// the introduction's heading is the tutorial's name, shown on its own
const introductionText = (tutorial:Tutorial) => (tutorial?.introduction || '').replace(/<h1>[\s\S]*?<\/h1>/, '').trim()

// the tutorials panel beside the tutorial model. It opens on the list of tutorials; a tutorial is a video and what to
// do for each step, with the model as it should be at that step. The tutorial model and its organisation are local to
// the page, so replacing them doesn't touch the user's own models
@Component({
    selector: 'app-tutorial',
    templateUrl: 'tutorial.html',
    imports: [MaterialModule, VideoComponent],
    standalone: true,
    styleUrls: ['tutorial.scss']
})
export class TutorialComponent implements OnInit {
    @Input() gcComponent:GraphComponentComponent
    @Input() linqset:Linqset
    @Input() organisation:Organisation
    @Input() organisationLinqset:Linqset

    tutorials:Tutorial[]
    tutorial:Tutorial                // the tutorial being followed, or none while choosing one
    step:number = 1
    tutorialStep:TutorialStep
    stepSummary = ''
    stepScript = ''
    showScript = true
    loading = false
    loadFailed = false

    progress:Progress = readProgress()
    showHowItWorks = false

    constructor(private repoService:RepositoryService, private userService:UserService) {}

    ngOnInit(): void {
        // how the tutorials work is explained the first time, and can be read again from the panel's ? button
        this.showHowItWorks = !this.userService.getUser()?.onboardingSteps?.includes(ONBOARDING_STEPS.TUTORIAL)
        this.getTutorials()
    }

    get stepCount() {
        return this.tutorial?.steps?.length || 0
    }

    get steps() {
        return this.tutorial?.steps || []
    }

    get title() {
        return stepTitle(this.tutorialStep)
    }

    get videoUrl() {
        return this.tutorial && `api/help/tutorials/${this.tutorial.type}/steps/${this.step}/video`
    }

    introduction(t:Tutorial) {
        return introductionText(t)
    }

    stepName(s:TutorialStep) {
        return stepTitle(s)
    }

    async getTutorials() {
        this.loading = true
        this.loadFailed = false
        try {
            // shown in the panel rather than a dialog, with a way to try again
            this.tutorials = await this.repoService.callSingleAPI(this.repoService.getData(`api/help/tutorials`), COMPONENT, "Get tutorials", undefined, undefined, undefined, undefined, false)
        }catch(err) {
            this.loadFailed = true
        }finally {
            this.loading = false
        }
    }

    gotIt() {
        this.showHowItWorks = false
        this.userService.saveOnboardingStep(ONBOARDING_STEPS.TUTORIAL)
    }

    // ---------- choosing a tutorial ----------

    progressFor(t:Tutorial) {
        return this.progress[t.type]
    }

    // a tutorial starts at its first step, with the canvas as it should be then, or carries on where it was left
    open(t:Tutorial, fromStart = false) {
        this.tutorial = t
        const saved = this.progressFor(t)
        const step = !fromStart && saved && !saved.done ? Math.min(saved.step, this.stepCount) : 1
        this.goTo(step, true)
    }

    backToList() {
        this.tutorial = undefined
        this.tutorialStep = undefined
    }

    // ---------- a tutorial's steps ----------

    // to a step; the canvas is set to how the model looks at its start when asked, or when starting a tutorial,
    // otherwise the user carries on with the model they're building
    goTo(n:number, resetModel = false) {
        this.step = Math.max(1, Math.min(n, this.stepCount))
        this.tutorialStep = this.tutorial?.steps?.[this.step-1]
        const {summary, script} = splitStepHtml(this.tutorialStep?.html)
        this.stepSummary = summary
        this.stepScript = script
        this.saveProgress()
        if (resetModel) this.replaceModelWithStep()
    }

    // the step's video plays as it loads, whichever way the step was reached
    nextStep() {
        if (this.step >= this.stepCount) return
        this.goTo(this.step + 1)
    }

    previousStep() {
        if (this.step <= 1) return
        this.goTo(this.step - 1)
    }

    restart() {
        this.goTo(1, true)
    }

    // the last step done: the tutorial is marked done in the list
    finish() {
        if (this.tutorial) {
            this.progress = {...this.progress, [this.tutorial.type]: {step: this.stepCount, done: true}}
            writeProgress(this.progress)
        }
        this.backToList()
    }

    private saveProgress() {
        if (!this.tutorial) return
        const done = this.progressFor(this.tutorial)?.done
        this.progress = {...this.progress, [this.tutorial.type]: {step: this.step, done}}
        writeProgress(this.progress)
    }

    // puts the canvas back to how the model looks at the start of this step
    async replaceModelWithStep() {
        if (!this.linqset || !this.tutorialStep) return
        const model = this.tutorialStep.model || {}
        this.linqset.nodes = model.nodes||[]
        this.linqset.edges = model.edges||[]
        this.linqset.nodes.forEach(n => {
            n.modelKey = TUTORIAL_MODEL_KEY
            n.projectKey = CURRENT_STATE_PROJECT_KEY
        })
        this.linqset.edges.forEach(e => {
            e.modelKey = TUTORIAL_MODEL_KEY
            e.projectKey = CURRENT_STATE_PROJECT_KEY
        })

        if (this.organisationLinqset) this.organisationLinqset.nodes = this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE || n.superType === NODE_SUPERTYPE.SYSTEM)

        await processLinqset(this.linqset, this.organisationLinqset, true)
        this.gcComponent?.newLinqset()
    }
}
